import * as React from "react";
import { useNavigate } from "react-router-dom";
import CclDrawer from "../../../components/common/cclDrawer/cclDrawer";
import {
    Alert,
    AlertColor,
    Autocomplete,
    Box,
    Stack,
    TextField,
    TextFieldProps,
    Typography,
} from "@mui/material";

import CclDrawerActionBar from "../../../components/common/cclDrawer/cclDrawerActionBar";
import CclButton from "../../../components/common/cclButtons/cclButton";
import CclLoadingButton from "../../../components/common/cclButtons/cclLoadingButton";
import { DatePicker } from "@mui/x-date-pickers";
import {
    useGetAvailableAssesmentsQuery,
    useCreateNewSessionMutation,
} from "../../../services/cclTokenedSessionApi";
import {
    CreateSessionRequest,
    SessionDetailType,
} from "../../../services/types/sessionDetailTypes";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import { AccessEventSessionDetails } from "../../../services/types/accessEventTypes";
import { getLocalIsoDateWithNoTime } from "../../../utilities/dateHelpers";

interface CreateSessionDrawerProps {
    open: boolean;
    onClose: () => void;
    showSnackbar: (message: string, severity: AlertColor) => void;
}

const searchInputSx = {
    "& .MuiOutlinedInput-input": {
        paddingTop: "8px!important",
        paddingBottom: "8px!important",
    },
    "& .MuiInputLabel-root": {
        marginTop: "-8px!important",
    },
    "& .MuiInputLabel-shrink": {
        marginTop: "auto!important",
    },
    flexGrow: 1,
};

const CreateSessionDrawer: React.FC<CreateSessionDrawerProps> = (props) => {
    const { logEvent } = useLogAccessEvent();
    const navigate = useNavigate();
    const { data: availableAssessments } = useGetAvailableAssesmentsQuery();
    const [create] = useCreateNewSessionMutation();
    const assessmentOptions = React.useMemo(
        () =>
            availableAssessments?.map((assessment: any) => ({
                id: assessment.buildPlanKey.toString(),
                label: assessment.name,
            })) ?? [],
        [availableAssessments],
    );

    const [title, setTitle] = React.useState<string>("");
    const [clientOrganization, setClientOrganization] = React.useState<string>("");
    const [creating, setCreating] = React.useState<boolean>(false);
    const [selectedAssessment, setSelectedAssessment] = React.useState<string>("");
    const [completionDate, setCompletionDate] = React.useState<Date | null>(null);
    const [sessionDate, setSessionDate] = React.useState<Date | null>(null);
    const [preworkInvitationDate, setPreworkInvitationDate] = React.useState<Date | null>(null);
    const [reminder1Date, setReminder1Date] = React.useState<Date | null>(null);
    const [reminder2Date, setReminder2Date] = React.useState<Date | null>(null);
    const [reminder3Date, setReminder3Date] = React.useState<Date | null>(null);

    // Custom change handlers to cascade clear invalid dates
    const handleSessionDateChange = (newDate: Date | null) => {
        setSessionDate(newDate);
        // If session date is cleared or becomes earlier/equal to completion date, clear completion and cascade
        if (!newDate || (completionDate && completionDate >= newDate)) {
            setCompletionDate(null);
            setPreworkInvitationDate(null);
            setReminder1Date(null);
            setReminder2Date(null);
            setReminder3Date(null);
        }
    };

    const handleCompletionDateChange = (newDate: Date | null) => {
        setCompletionDate(newDate);
        // If completion date is cleared or becomes invalid, clear dependent fields
        if (!newDate) {
            setPreworkInvitationDate(null);
            setReminder1Date(null);
            setReminder2Date(null);
            setReminder3Date(null);
        } else {
            // Check each dependent field and clear if now invalid
            if (preworkInvitationDate && preworkInvitationDate >= newDate) {
                setPreworkInvitationDate(null);
            }
            if (reminder1Date && reminder1Date >= newDate) {
                setReminder1Date(null);
                setReminder2Date(null);
                setReminder3Date(null);
            } else if (reminder2Date && reminder2Date >= newDate) {
                setReminder2Date(null);
                setReminder3Date(null);
            } else if (reminder3Date && reminder3Date >= newDate) {
                setReminder3Date(null);
            }
        }
    };

    const handleInvitationDateChange = (newDate: Date | null) => {
        setPreworkInvitationDate(newDate);
        // If invitation becomes later than or equal to reminder 1, clear reminder 1 and cascade
        if (newDate && reminder1Date && reminder1Date <= newDate) {
            setReminder1Date(null);
            setReminder2Date(null);
            setReminder3Date(null);
        }
    };

    const handleReminder1Change = (newDate: Date | null) => {
        setReminder1Date(newDate);
        // If reminder 1 is cleared or becomes invalid, clear dependent reminders
        if (!newDate) {
            setReminder2Date(null);
            setReminder3Date(null);
        } else if (reminder2Date && reminder2Date <= newDate) {
            setReminder2Date(null);
            setReminder3Date(null);
        }
    };

    const handleReminder2Change = (newDate: Date | null) => {
        setReminder2Date(newDate);
        // If reminder 2 is cleared or becomes invalid, clear reminder 3
        if (!newDate) {
            setReminder3Date(null);
        } else if (reminder3Date && reminder3Date <= newDate) {
            setReminder3Date(null);
        }
    };

    // Helper function to get today at midnight
    const getTodayStart = (): Date => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    };

    // Validation function that returns error messages
    const validateAllFields = (): string[] => {
        const messages: string[] = [];
        const today = getTodayStart();

        // Session Date: Must be in the future
        if (sessionDate && sessionDate < today) {
            messages.push("Session Date must be in the future");
        }

        // Assessment Completion: Must be before Session Date and in the future
        if (completionDate) {
            if (completionDate < today) {
                messages.push("Assessment Completion must be in the future");
            } else if (sessionDate && completionDate >= sessionDate) {
                messages.push(
                    "Assessment Completion must be before Session Date and in the future",
                );
            }
        }

        // Invitation (Scheduled): Must be before Assessment Completion and in the future
        if (preworkInvitationDate) {
            if (preworkInvitationDate < today) {
                messages.push("Invitation (Scheduled) must be in the future");
            } else if (completionDate && preworkInvitationDate >= completionDate) {
                messages.push(
                    "Invitation (Scheduled) must be before Assessment Completion and in the future",
                );
            }
        }

        // Reminders validation - single message for all reminders
        const reminders = [
            { date: reminder1Date, num: 1 },
            { date: reminder2Date, num: 2 },
            { date: reminder3Date, num: 3 },
        ];
        const invalidReminders = reminders.filter((r) => r.date !== null);

        if (invalidReminders.length > 0) {
            let hasReminderError = false;

            if (preworkInvitationDate) {
                // If Invitation is set: Reminders must be after Invitation and before Assessment Completion
                for (const reminder of invalidReminders) {
                    if (reminder.date) {
                        if (
                            reminder.date <= preworkInvitationDate ||
                            (completionDate && reminder.date >= completionDate)
                        ) {
                            hasReminderError = true;
                            break;
                        }
                    }
                }
                if (hasReminderError) {
                    messages.push(
                        "Reminders must be between Invitation (Scheduled) and Assessment Completion",
                    );
                }
            } else {
                // If Invitation is NOT set: Reminders must be in the future and before Assessment Completion
                for (const reminder of invalidReminders) {
                    if (reminder.date) {
                        if (
                            reminder.date < today ||
                            (completionDate && reminder.date >= completionDate)
                        ) {
                            hasReminderError = true;
                            break;
                        }
                    }
                }
                if (hasReminderError) {
                    messages.push(
                        "Reminders must be in the future and before Assessment Completion",
                    );
                }
            }
        }

        return messages;
    };

    // Compute validation messages synchronously whenever dates change
    const validationMessages = React.useMemo(
        () => validateAllFields(),
        [
            sessionDate,
            completionDate,
            preworkInvitationDate,
            reminder1Date,
            reminder2Date,
            reminder3Date,
        ],
    );

    const validateSessionData = (): boolean => {
        // Check that required fields are present
        if (!title || !selectedAssessment || !sessionDate || !completionDate) return false;

        // Check if there are any validation messages
        if (validationMessages.length > 0) return false;

        return true;
    };

    const createSession = () => {
        if (!validateSessionData()) return;

        let r1 = reminder1Date ? getLocalIsoDateWithNoTime(reminder1Date) : null;
        let r2 = reminder2Date ? getLocalIsoDateWithNoTime(reminder2Date) : null;
        let r3 = reminder3Date ? getLocalIsoDateWithNoTime(reminder3Date) : null;

        let payload: CreateSessionRequest = {
            title,
            clientName: clientOrganization,
            buildPlanKey: Number(selectedAssessment),
            startDate: sessionDate ? getLocalIsoDateWithNoTime(sessionDate) : null,
            endDate: sessionDate ? getLocalIsoDateWithNoTime(sessionDate) : null,
            surveyCompletion: getLocalIsoDateWithNoTime(completionDate!),
            autoInvitationDate: preworkInvitationDate
                ? getLocalIsoDateWithNoTime(preworkInvitationDate)
                : null,
            reminderDates: [r1, r2, r3].filter((date) => date !== null) as string[],
        };
        setCreating(true);

        create(payload)
            .unwrap()
            .then((response: SessionDetailType) => {
                setCreating(false);
                if (response?.sessionKey != null) {
                    let evtData: AccessEventSessionDetails = {
                        sessionID: response.sessionKey.toString(),
                        projectCode: response.sessionCode, // will be null/empty for product sales
                    };
                    logEvent("SessionCreated", evtData);
                    props.showSnackbar("Session Created", "success");
                    props.onClose();
                    navigate(`/session/${response.sessionKey}`);
                } else {
                    props.showSnackbar("Session Creation Failed", "error");
                }
            })
            .catch(() => {
                setCreating(false);
                props.showSnackbar("Session Creation Failed", "error");
            });
    };

    return (
        <CclDrawer
            title={"Create Session"}
            open={props.open}
            onClose={props.onClose}
            width="standard"
        >
            <Box sx={{ overflow: "hidden", height: 1 }}>
                <Stack spacing={2} sx={{ overflow: "hidden" }}>
                    <Typography variant="h6" sx={{ borderBottom: "1px solid gray", padding: 1 }}>
                        {" "}
                        Information{" "}
                    </Typography>

                    <TextField
                        fullWidth
                        label="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />

                    <TextField
                        fullWidth
                        label="Client Organization"
                        value={clientOrganization}
                        onChange={(e) => setClientOrganization(e.target.value)}
                    />

                    <Autocomplete
                        fullWidth
                        options={assessmentOptions}
                        getOptionLabel={(option: any) => option.label}
                        onChange={(event, newValue) =>
                            setSelectedAssessment(newValue ? newValue.id : "")
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Assessment" placeholder="Assessment" />
                        )}
                    />

                    <Typography
                        variant="h6"
                        sx={{
                            borderBottom: "1px solid gray",
                            padding: 1,
                            marginTop: "20px !important",
                        }}
                    >
                        {" "}
                        Timeline
                    </Typography>

                    <DatePicker
                        label={"Session Date"}
                        value={sessionDate}
                        minDate={getTodayStart()}
                        renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                            params.fullWidth = true;
                            return (
                                <TextField {...params} onKeyDown={(e) => {}} sx={searchInputSx} />
                            );
                        }}
                        onChange={(e) => handleSessionDateChange(e)}
                    />

                    <DatePicker
                        label={"Assessment Completion"}
                        value={completionDate}
                        minDate={getTodayStart()}
                        maxDate={sessionDate || undefined}
                        renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                            params.fullWidth = true;
                            return (
                                <TextField {...params} onKeyDown={(e) => {}} sx={searchInputSx} />
                            );
                        }}
                        onChange={(e) => handleCompletionDateChange(e)}
                    />

                    <DatePicker
                        label={"Invitation (Scheduled)"}
                        value={preworkInvitationDate}
                        minDate={getTodayStart()}
                        maxDate={completionDate || undefined}
                        renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                            params.fullWidth = true;
                            return (
                                <TextField {...params} onKeyDown={(e) => {}} sx={searchInputSx} />
                            );
                        }}
                        onChange={(e) => handleInvitationDateChange(e)}
                    />

                    <DatePicker
                        label={"Reminder 1"}
                        value={reminder1Date}
                        minDate={preworkInvitationDate || getTodayStart()}
                        maxDate={completionDate || undefined}
                        renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                            params.fullWidth = true;
                            return (
                                <TextField {...params} onKeyDown={(e) => {}} sx={searchInputSx} />
                            );
                        }}
                        onChange={(e) => handleReminder1Change(e)}
                    />

                    <DatePicker
                        label={"Reminder 2"}
                        value={reminder2Date}
                        minDate={reminder1Date || undefined}
                        maxDate={completionDate || undefined}
                        disabled={!reminder1Date}
                        renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                            params.fullWidth = true;
                            return (
                                <TextField {...params} onKeyDown={(e) => {}} sx={searchInputSx} />
                            );
                        }}
                        onChange={(e) => handleReminder2Change(e)}
                    />

                    <DatePicker
                        label={"Reminder 3"}
                        value={reminder3Date}
                        minDate={reminder2Date || undefined}
                        maxDate={completionDate || undefined}
                        disabled={!reminder2Date}
                        renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                            params.fullWidth = true;
                            return (
                                <TextField {...params} onKeyDown={(e) => {}} sx={searchInputSx} />
                            );
                        }}
                        onChange={(e) => setReminder3Date(e)}
                    />

                    {validationMessages.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            {validationMessages.map((message, index) => (
                                <Alert key={index} severity="info" sx={{ mb: 1 }}>
                                    {message}
                                </Alert>
                            ))}
                        </Box>
                    )}
                </Stack>
                <CclDrawerActionBar>
                    <CclLoadingButton
                        mode={"primary"}
                        onClick={createSession}
                        loading={creating}
                        disabled={!validateSessionData()}
                    >
                        Create
                    </CclLoadingButton>
                    <CclButton
                        sx={{ float: "right", mr: 4 }}
                        mode={"secondary"}
                        onClick={props.onClose}
                    >
                        Cancel
                    </CclButton>
                </CclDrawerActionBar>
            </Box>
        </CclDrawer>
    );
};

export default CreateSessionDrawer;
